import Box from '@mui/material/Box';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * Reusable markdown renderer.
 *
 * Security: `react-markdown` escapes raw HTML by default, so user input
 * cannot inject scripts. We don't enable `rehype-raw`. Links are rendered
 * with `rel="noopener noreferrer"` and open in a new tab.
 *
 * Styling: MUI-flavored typography via a thin `sx` wrapper instead of a
 * components map, so the tree stays small. Links use the theme's primary
 * color, code blocks use the monospace font.
 */

export interface MarkdownViewProps {
  children: string;
}

export function MarkdownView({ children }: MarkdownViewProps) {
  return (
    <Box
      sx={{
        '& p': { my: 1, lineHeight: 1.65 },
        '& p:first-of-type': { mt: 0 },
        '& p:last-child': { mb: 0 },
        '& a': {
          color: 'primary.main',
          textDecoration: 'underline',
        },
        '& code': {
          fontFamily: 'monospace',
          fontSize: '0.9em',
          bgcolor: 'action.hover',
          px: 0.75,
          py: 0.25,
          borderRadius: 0.5,
        },
        '& pre': {
          bgcolor: 'action.hover',
          p: 1.5,
          borderRadius: 1,
          overflowX: 'auto',
          my: 1.5,
        },
        '& pre code': {
          bgcolor: 'transparent',
          p: 0,
        },
        '& blockquote': {
          borderLeft: '3px solid',
          borderColor: 'primary.main',
          pl: 2,
          ml: 0,
          my: 1.5,
          color: 'text.secondary',
          fontStyle: 'italic',
        },
        '& ul, & ol': {
          pl: 3,
          my: 1,
        },
        '& li': { my: 0.25 },
        '& h1, & h2, & h3, & h4': { mt: 2, mb: 1, fontWeight: 600 },
        '& h1': { fontSize: '1.5rem' },
        '& h2': { fontSize: '1.3rem' },
        '& h3': { fontSize: '1.15rem' },
        '& h4': { fontSize: '1rem' },
        '& hr': {
          border: 'none',
          borderTop: '1px solid',
          borderColor: 'divider',
          my: 2,
        },
        '& table': {
          borderCollapse: 'collapse',
          my: 1.5,
          '& th, & td': {
            border: '1px solid',
            borderColor: 'divider',
            px: 1.5,
            py: 0.75,
          },
          '& th': {
            bgcolor: 'action.hover',
            fontWeight: 600,
          },
        },
        '& img': {
          maxWidth: '100%',
          borderRadius: 1,
        },
      }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ node, children: linkChildren, ...props }) => (
            // eslint-disable-next-line jsx-a11y/anchor-has-content
            <a {...props} target="_blank" rel="noopener noreferrer">
              {linkChildren}
            </a>
          ),
        }}
      >
        {children}
      </ReactMarkdown>
    </Box>
  );
}
